import React, { useCallback, useMemo } from 'react'

type PaginationButtonsProps = {
  currentPage: number
  maxPage: number
  goToPage: (page: number) => void
}

const PaginationButtons = React.memo(function PaginationButtons({
  currentPage,
  maxPage,
  goToPage,
}: PaginationButtonsProps) {
  const handleGoToPage = useCallback((page: number) => () => goToPage(page), [goToPage])

  const renderButton = useCallback(
    (page: number) => (
      <button
        key={page}
        type="button"
        onClick={handleGoToPage(page)}
        aria-label={`Go to page ${page}`}
        aria-current={currentPage === page ? 'page' : undefined}
        className={`flex h-12 w-12 items-center justify-center rounded border hover:border-transparent hover:bg-primary-green-85 hover:text-white ${
          currentPage === page
            ? 'border-transparent bg-primary-green-85 text-white'
            : 'border-secondary-gray-150 bg-secondary-gray-100'
        }`}>
        {page}
      </button>
    ),
    [currentPage, handleGoToPage],
  )

  const buttons = useMemo(() => {
    const result = []
    const showEllipsis = maxPage > 7

    if (showEllipsis) {
      if (currentPage <= 4) {
        for (let i = 1; i <= 5; i++) {
          result.push(renderButton(i))
        }
        result.push(
          <span key="ellipsis1" className="mx-2 flex items-center justify-center" aria-hidden>
            ...
          </span>,
        )
        result.push(renderButton(maxPage))
      } else if (currentPage >= maxPage - 3) {
        result.push(renderButton(1))
        result.push(
          <span key="ellipsis2" className="mx-2 flex items-center justify-center" aria-hidden>
            ...
          </span>,
        )
        for (let i = maxPage - 4; i <= maxPage; i++) {
          result.push(renderButton(i))
        }
      } else {
        result.push(renderButton(1))
        result.push(
          <span key="ellipsis3" className="mx-2 flex items-center justify-center" aria-hidden>
            ...
          </span>,
        )
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          result.push(renderButton(i))
        }
        result.push(
          <span key="ellipsis4" className="mx-2 flex items-center justify-center" aria-hidden>
            ...
          </span>,
        )
        result.push(renderButton(maxPage))
      }
    } else {
      for (let i = 1; i <= maxPage; i++) {
        result.push(renderButton(i))
      }
    }

    return result
  }, [currentPage, maxPage, renderButton])

  return <>{buttons}</>
})

export default PaginationButtons
